/**
 * @flow
 */

import React, { useEffect, useCallback, useMemo } from 'react';
import gql from 'graphql-tag';
import { enableExperimentalFragmentVariables } from 'graphql-tag';
import { useQuery, useApolloClient } from '@apollo/client';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Loader } from 'semantic-ui-react';
import styled from 'styled-components';
import { format, isAfter, startOfDay } from 'date-fns';
import {
    get,
    find,
    cloneDeep,
    remove,
    findIndex,
    map,
    assign,
    pick,
    filter,
    each,
    some,
    isEmpty,
    uniqBy,
    property,
} from 'lodash';

import { setMaster } from '../../../../actions/master-appointments';
import { setSettings } from '../../../../actions/salon';
import { getScheduledServices } from '../../../../lib/schedule';
import Timetable from './Timetable';
import AppointmentCell from './AppointmentCell';
import AppointmentCardContainer from './AppointmentCardContainer';
import AppointmentColumn from '../../containers/timetable/AppointmentColumn';
import { Header } from '../../../../components/timetable';
import { MasterTypeIcon } from '../../../../components';

import {
    APPOINTMENT_FRAGMENT,
    APPOINTMENT_ADD_SUBSCRIPTION,
    APPOINTMENT_DELETE_SUBSCRIPTION,
    MASTER_MIGRATION_SUBSCRIPTION,
} from '../../containers/queries';

import { MASTER_TABLE_FRAGMENT } from '../../containers/masterAccessQueries';

// The query to get current salon object
export const GET_SALON_QUERY = gql`
    query getSalon(
        $date: String!
        $salon: ID!
        $showArchive: Boolean
        $lang: String!
    ) {
        viewer {
            id
            ... on Administrator {
                salon(id: $salon) {
                    id
                    name
                    administrator {
                        id
                    }
                    settings {
                        schedule {
                            startAt
                            endAt
                            step
                            format
                        }
                        enableArchive
                    }
                    masters(date: $date) {
                        pageInfo {
                            endCursor
                            hasNextPage
                        }
                        nodes {
                            id
                            ...master
                        }
                    }
                }
            }
            ...onMasterTable
        }
    }

    fragment master on Master {
        firstName
        lastName
        phone
        email
        status
        type
        serviceGroups {
            id
            name(lang: $lang)
            type
        }
        appointments(date: $date, showArchive: $showArchive) {
            id
            ...appointment
        }
        schedule(startDate: $date) {
            id
            startAt
            endAt
        }
    }

    ${APPOINTMENT_FRAGMENT}
    ${MASTER_TABLE_FRAGMENT}
`;

export const renderCards = ({
    provider,
    salon,
    updateAppointmentClient,
    updateAppointment,
    archiveAppointment,
    inCabinet,
}: Object) => {
    if (!provider.appointments.length) {
        return null;
    }

    // fixes flattenChildren error after adding appointment.
    // TODO: need to fix this error in other place, this fix is crutch.
    const appointments = uniqBy(provider.appointments, property('id'));

    return (
        <AppointmentCardContainer
            appointments={appointments}
            salon={salon}
            updateAppointment={updateAppointment}
            updateAppointmentClient={updateAppointmentClient}
            inCabinet={inCabinet}
            archiveAppointment={archiveAppointment}
        />
    );
};

const MasterTable = () => {
    const apolloClient = useApolloClient();
    const history = useHistory();
    const dispatch = useDispatch();

    const userId = useSelector(({ user }) => user.get('id'));
    const date = useSelector(({ appointments }) => appointments.get('date'));
    const salonId = useSelector(({ user }) => user.get('salon'));
    const showArchive = useSelector(({ salon }) => salon.get('showArchive'));
    const currentLanguage = useSelector(({ intl }) => intl.get('locale') || intl.get('defaultLanguage'));
    const isOwner = useSelector(({ user }) => user.get('isOwner'));
    const isSysadmin = useSelector(({ user }) => user.get('isSysadmin'));
    const isMaster = useSelector(({ user }) => user.get('isMaster'));
    const allowToAddAppointments =
        isOwner || !isAfter(startOfDay(new Date()), startOfDay(new Date(date)));

    const queryVariables = {
        date: format(new Date(date), 'yyyy-MM-dd'),
        salon: salonId,
        showArchive,
        lang: currentLanguage,
    };

    const { data, loading, subscribeToMore } = useQuery(GET_SALON_QUERY, {
        variables: queryVariables,
        fetchPolicy: 'cache-and-network',
        skip: !salonId,
    });

    const salon = get(data, 'viewer.salon');
    const masters = useMemo(
        () => filter(get(salon, 'masters.nodes'), m => !!m.schedule.length),
        [salon],
    );
    const services = useMemo(() => getScheduledServices(masters), [masters]);

    // Side effect: setSettings in sysadmin mode
    useEffect(() => {
        if (
            !loading &&
            salon &&
            isSysadmin &&
            !isEmpty(salon) &&
            salon.administrator.id !== userId
        ) {
            const salonSettings = Object.assign(
                { enableArchive: get(salon, 'settings.enableArchive') },
                get(salon, 'settings.schedule'),
            );
            dispatch(setSettings(salonSettings));
        }
    }, [loading, salon, isSysadmin, userId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Subscriptions
    useEffect(() => {
        if (!salonId) return;
        const dateStr = format(new Date(date), 'yyyy-MM-dd');

        const unsubAdd = subscribeToMore({
            document: APPOINTMENT_ADD_SUBSCRIPTION,
            variables: {
                salon: salonId,
                date: dateStr,
                lang: currentLanguage,
            },
            updateQuery: (prev, { subscriptionData }) => {
                if (!subscriptionData) {
                    return prev;
                }

                const next = cloneDeep(prev);
                const appointment = subscriptionData.data.appointmentAdded;

                if (
                    (showArchive || !appointment.archived) &&
                    appointment.master &&
                    some(next.viewer.salon.masters.nodes, {
                        id: appointment.master.id,
                    })
                ) {
                    each(next.viewer.salon.masters.nodes, (master, key) => {
                        const index = findIndex(master.appointments, [
                            'id',
                            appointment.id,
                        ]);
                        if (index > -1) {
                            if (appointment.master.id == master.id) {
                                next.viewer.salon.masters.nodes[
                                    key
                                ].appointments[index] = appointment;
                            } else {
                                remove(
                                    next.viewer.salon.masters.nodes[key]
                                        .appointments,
                                    ['id', appointment.id],
                                );
                            }
                        } else {
                            if (appointment.master.id == master.id) {
                                next.viewer.salon.masters.nodes[
                                    key
                                ].appointments.push(appointment);
                            }
                        }
                    });
                }

                return next;
            },
        });

        const unsubDelete = subscribeToMore({
            document: APPOINTMENT_DELETE_SUBSCRIPTION,
            variables: {
                salon: salonId,
                date: dateStr,
                lang: currentLanguage,
            },
            updateQuery: (prev, { subscriptionData }) => {
                if (!subscriptionData) {
                    return prev;
                }

                const next = cloneDeep(prev);
                const appointment = subscriptionData.data.appointmentDeleted;

                let master = null;
                // check if appointment has master (related to #312)
                if (appointment.master) {
                    master = find(next.viewer.salon.masters.nodes, {
                        id: appointment.master.id,
                    });
                }

                if (master) {
                    remove(master.appointments, o => o.id === appointment.id);
                }

                return next;
            },
        });

        const unsubMigrate = subscribeToMore({
            document: MASTER_MIGRATION_SUBSCRIPTION,
            variables: {
                salon: salonId,
            },
            updateQuery: (prev, { subscriptionData }) => {
                if (!subscriptionData) {
                    return prev;
                }

                const next = cloneDeep(prev);
                const masterMigrated = get(
                    subscriptionData,
                    'data.masterMigrated',
                );

                const master = find(next.viewer.salon.masters.nodes, {
                    phone: masterMigrated.phone,
                });

                const appointments = map(get(master, 'appointments'), item => {
                    const newItem = cloneDeep(item);
                    newItem.master = assign(
                        item.master,
                        pick(masterMigrated, ['id', 'firstName', 'lastName']),
                    );
                    return newItem;
                });

                master.appointments = appointments;
                master.firstName = masterMigrated.firstName;
                master.lastName = masterMigrated.lastName;

                return next;
            },
        });

        return () => {
            unsubAdd();
            unsubDelete();
            unsubMigrate();
        };
    }, [salonId, date, showArchive, currentLanguage]); // eslint-disable-line react-hooks/exhaustive-deps

    const updateAppointmentClient = useCallback(
        client => {
            const variables = {
                date: format(new Date(date), 'yyyy-MM-dd'),
                salon: salonId,
                showArchive,
                lang: currentLanguage,
            };
            const cacheData = apolloClient.cache.readQuery({
                query: GET_SALON_QUERY,
                variables,
            });
            const masterNodes = cacheData.viewer.salon.masters.nodes || [];
            const nextMasters = masterNodes.map(master => {
                const appointments = master.appointments || [];
                const nextAppointments = appointments.map(appointment => {
                    if (appointment.client.id === client.id) {
                        return {
                            ...appointment,
                            notes: client.notes,
                        };
                    }
                    return appointment;
                });
                return {
                    ...master,
                    appointments: nextAppointments,
                };
            });
            const nextData = {
                ...cacheData,
                viewer: {
                    ...cacheData.viewer,
                    salon: {
                        ...cacheData.viewer.salon,
                        masters: {
                            ...cacheData.viewer.salon.masters,
                            nodes: nextMasters,
                        },
                    },
                },
            };
            apolloClient.cache.writeQuery({
                query: GET_SALON_QUERY,
                variables,
                data: nextData,
            });
        },
        [date, salonId, showArchive, currentLanguage, apolloClient],
    );

    const onHeaderClick = useCallback(
        master => {
            if (!isMaster) {
                dispatch(setMaster(master));
                history.push('/salon/master-appointments');
            }
        },
        [isMaster, history, dispatch],
    );

    const createAppointments = useCallback(
        appointments => {
            const variables = {
                date: format(new Date(date), 'yyyy-MM-dd'),
                salon: salonId,
                showArchive,
                lang: currentLanguage,
            };
            const cacheData = apolloClient.cache.readQuery({
                query: GET_SALON_QUERY,
                variables,
            });

            const masterNodes = cacheData.viewer.salon.masters.nodes || [];
            const nextMasters = masterNodes.map(master => {
                const newMasterAppointments = appointments
                    .filter(app => app.master && app.master.id === master.id)
                    .map(app => ({
                        ...app,
                        invoice: null,
                    }));
                if (newMasterAppointments.length === 0) {
                    return master;
                }
                return {
                    ...master,
                    appointments: [
                        ...(master.appointments || []),
                        ...newMasterAppointments,
                    ],
                };
            });
            const nextData = {
                ...cacheData,
                viewer: {
                    ...cacheData.viewer,
                    salon: {
                        ...cacheData.viewer.salon,
                        masters: {
                            ...cacheData.viewer.salon.masters,
                            nodes: nextMasters,
                        },
                    },
                },
            };

            apolloClient.cache.writeQuery({
                query: GET_SALON_QUERY,
                variables,
                data: nextData,
            });
        },
        [date, salonId, showArchive, currentLanguage, apolloClient],
    );

    const updateAppointment = useCallback(
        appointment => {
            const variables = {
                date: format(new Date(date), 'yyyy-MM-dd'),
                salon: salonId,
                showArchive,
                lang: currentLanguage,
            };
            const cacheData = apolloClient.cache.readQuery({
                query: GET_SALON_QUERY,
                variables,
            });

            let nextMasters = cacheData.viewer.salon.masters.nodes || [];
            if (
                (showArchive || !appointment.archived) &&
                some(nextMasters, { id: appointment.master.id })
            ) {
                nextMasters = nextMasters.map(master => {
                    const appointments = master.appointments || [];
                    const hasApp = appointments.some(
                        item => item.id === appointment.id,
                    );

                    if (master.id === appointment.master.id) {
                        if (hasApp) {
                            return {
                                ...master,
                                appointments: appointments.map(item =>
                                    item.id === appointment.id
                                        ? appointment
                                        : item,
                                ),
                            };
                        } else {
                            return {
                                ...master,
                                appointments: [...appointments, appointment],
                            };
                        }
                    } else {
                        if (hasApp) {
                            return {
                                ...master,
                                appointments: appointments.filter(
                                    item => item.id !== appointment.id,
                                ),
                            };
                        }
                        return master;
                    }
                });
            }

            const nextData = {
                ...cacheData,
                viewer: {
                    ...cacheData.viewer,
                    salon: {
                        ...cacheData.viewer.salon,
                        masters: {
                            ...cacheData.viewer.salon.masters,
                            nodes: nextMasters,
                        },
                    },
                },
            };

            apolloClient.cache.writeQuery({
                query: GET_SALON_QUERY,
                variables,
                data: nextData,
            });
        },
        [date, salonId, showArchive, currentLanguage, apolloClient],
    );

    if (!salon && loading) {
        return <Loader size="big" inline="centered" active />;
    }
    if (!salon) {
        return null;
    }

    return (
        <Timetable
            salon={salon}
            loading={loading}
            date={date}
            renderColumn={columnProps => (
                <AppointmentColumn
                    {...columnProps}
                    updateAppointment={updateAppointment}
                />
            )}
            renderCell={cellProps => (
                <AppointmentCell
                    key={cellProps.time}
                    salon={salon}
                    date={date}
                    allowToAddAppointments={allowToAddAppointments}
                    {...cellProps}
                />
            )}
            renderCards={({ provider }) =>
                renderCards({
                    provider,
                    salon,
                    updateAppointmentClient,
                    updateAppointment,
                })
            }
            renderHeader={({ provider }) => (
                <Header
                    provider={provider}
                    headerText={`${provider.firstName} ${provider.lastName ||
                        ''}`}
                    onHeaderClick={onHeaderClick}
                >
                    <MasterTypeIcon
                        name={`${provider.firstName} ${provider.lastName ||
                            ''}`}
                        type={property(['serviceGroups', '0', 'type'])(
                            provider,
                        )}
                    />
                </Header>
            )}
            providers={masters}
            services={services}
            allowToAddAppointments={allowToAddAppointments}
            createAppointments={createAppointments}
        />
    );
};

export default MasterTable;
